import React, { useMemo, useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { fetchTechServicesExport } from '../services/techServicesService';
import { sanitizeString } from '../utils/security';

/** Sütun tanımları — key, Excel başlığı, seçim grubu */
export const TECH_SERVICE_EXPORT_COLUMNS = [
  { key: 'siraNo', label: 'Sıra No', group: 'Genel', defaultOn: true, locked: true },
  { key: 'kod', label: 'Teknik servis kodu', group: 'Genel', defaultOn: true },
  { key: 'ad', label: 'Teknik servis adı', group: 'Genel', defaultOn: true },
  { key: 'aktif', label: 'Durum (aktif)', group: 'Genel', defaultOn: false },
  { key: 'adres', label: 'Adres', group: 'Lokasyon / iletişim', defaultOn: true },
  { key: 'ilAdi', label: 'İl', group: 'Lokasyon / iletişim', defaultOn: true },
  { key: 'ilceAdi', label: 'İlçe', group: 'Lokasyon / iletişim', defaultOn: true },
  { key: 'cityId', label: 'İl plaka (ID)', group: 'Lokasyon / iletişim', defaultOn: false },
  { key: 'districtId', label: 'İlçe ID', group: 'Lokasyon / iletişim', defaultOn: false },
  { key: 'telefon', label: 'Telefon', group: 'Lokasyon / iletişim', defaultOn: true },
  { key: 'mail', label: 'E-posta', group: 'Lokasyon / iletişim', defaultOn: true },
  { key: 'yetkiliKisi', label: 'Yetkili kişi', group: 'Lokasyon / iletişim', defaultOn: false },
  { key: 'yetkiliTelefon', label: 'Yetkili telefon', group: 'Lokasyon / iletişim', defaultOn: false },
  { key: 'faks', label: 'Faks', group: 'Lokasyon / iletişim', defaultOn: false },
  { key: 'konumEnlem', label: 'Enlem', group: 'Konum (harita)', defaultOn: false },
  { key: 'konumBoylam', label: 'Boylam', group: 'Konum (harita)', defaultOn: false },
  { key: 'konumZoom', label: 'Harita zoom', group: 'Konum (harita)', defaultOn: false },
  { key: 'katilmaTarihi', label: 'Katılım tarihi', group: 'Sistem', defaultOn: false },
  { key: 'yolDegeri', label: 'Yol değeri (TL/km)', group: 'Sistem', defaultOn: false },
  { key: 'aciklama', label: 'Açıklama', group: 'Sistem', defaultOn: false },
  { key: 'createdAt', label: 'Oluşturulma', group: 'Sistem', defaultOn: false },
  { key: 'updatedAt', label: 'Güncellenme', group: 'Sistem', defaultOn: false },
  { key: 'sirketBilgileriText', label: 'Şirket bilgileri (özet)', group: 'Şirket ve banka', defaultOn: true },
  { key: 'bankaBilgileriText', label: 'Banka bilgileri (özet)', group: 'Şirket ve banka', defaultOn: true },
  { key: 'exportPanelKullanicilarText', label: 'Panel kullanıcıları', group: 'İlişkiler', defaultOn: true },
  { key: 'exportTeknisyenlerText', label: 'Teknisyenler', group: 'İlişkiler', defaultOn: true },
  { key: 'exportYetkiliAlanlarText', label: 'Yetkili alanlar', group: 'İlişkiler', defaultOn: true },
];

function safeTxt(v) {
  return sanitizeString(v ?? '');
}

function formatTrDateTime(v) {
  if (v == null || v === '') return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(v);
  }
}

function formatTrDateOnly(v) {
  if (v == null || v === '') return '';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString('tr-TR');
  } catch {
    return String(v);
  }
}

function ilAd(ts) {
  return safeTxt(ts.cityName || (ts.il && ts.il.name) || '');
}

function ilceAd(ts) {
  return safeTxt(ts.districtName || (ts.ilce && ts.ilce.name) || '');
}

function buildSirketText(ts) {
  const arr = ts.sirketBilgileri;
  if (!Array.isArray(arr) || arr.length === 0) return '';
  return arr
    .map((s, i) => {
      const tip = safeTxt(s.sirketTipi);
      const ad = safeTxt(s.sirketAdi);
      const vd = safeTxt(s.vergiDairesi);
      const vn = safeTxt(s.vergiNumarasi);
      const sad = safeTxt(s.sirketAdresi);
      return `#${i + 1} ${tip}: ${ad} | VD:${vd} VNo:${vn} | ${sad}`;
    })
    .join('\n');
}

function buildBankaText(ts) {
  const arr = ts.bankaBilgileri;
  if (!Array.isArray(arr) || arr.length === 0) return '';
  return arr
    .map((b, i) => {
      const bn = safeTxt(b.bankaName);
      const ha = safeTxt(b.hesapAdi);
      const ib = safeTxt(b.iban);
      return `#${i + 1} ${bn} | ${ha} | ${ib}`;
    })
    .join('\n');
}

function pickCell(ts, idx, key) {
  switch (key) {
    case 'siraNo':
      return idx + 1;
    case 'kod':
      return ts.kod != null ? ts.kod : '';
    case 'ad':
      return safeTxt(ts.ad);
    case 'aktif':
      return ts.aktif === false ? 'Pasif' : 'Aktif';
    case 'adres':
      return safeTxt(ts.adres);
    case 'ilAdi':
      return ilAd(ts);
    case 'ilceAdi':
      return ilceAd(ts);
    case 'cityId':
      return ts.cityId != null ? ts.cityId : '';
    case 'districtId':
      return ts.districtId != null ? ts.districtId : '';
    case 'telefon':
      return safeTxt(ts.telefon);
    case 'mail':
      return safeTxt(ts.mail);
    case 'yetkiliKisi':
      return safeTxt(ts.yetkiliKisi);
    case 'yetkiliTelefon':
      return safeTxt(ts.yetkiliTelefon);
    case 'faks':
      return safeTxt(ts.faks);
    case 'konumEnlem':
      return ts.konum?.koordinat?.enlem != null ? ts.konum.koordinat.enlem : '';
    case 'konumBoylam':
      return ts.konum?.koordinat?.boylam != null ? ts.konum.koordinat.boylam : '';
    case 'konumZoom':
      return ts.konum?.zoom != null ? ts.konum.zoom : '';
    case 'katilmaTarihi':
      return formatTrDateOnly(ts.katilmaTarihi);
    case 'yolDegeri':
      return ts.yolDegeri != null ? ts.yolDegeri : '';
    case 'aciklama':
      return safeTxt(ts.aciklama);
    case 'createdAt':
      return formatTrDateTime(ts.createdAt);
    case 'updatedAt':
      return formatTrDateTime(ts.updatedAt);
    case 'sirketBilgileriText':
      return buildSirketText(ts);
    case 'bankaBilgileriText':
      return buildBankaText(ts);
    case 'exportPanelKullanicilarText':
      return safeTxt(ts.exportPanelKullanicilarText);
    case 'exportTeknisyenlerText':
      return safeTxt(ts.exportTeknisyenlerText);
    case 'exportYetkiliAlanlarText':
      return safeTxt(ts.exportYetkiliAlanlarText);
    default:
      return '';
  }
}

function buildWorkbook(rows, labels, metaLine1, metaLine2, headerOffset = 4) {
  const aoa = [[metaLine1], [metaLine2], [], labels, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const nc = labels.length;
  ws['!cols'] = [];
  for (let j = 0; j < nc; j += 1) {
    let m = String(labels[j]).length;
    for (let r = 0; r < Math.min(rows.length, 150); r += 1) {
      const cell = rows[r][j];
      const len = cell == null ? 0 : String(cell).replace(/\s+/g, ' ').length;
      if (len > m) m = len;
    }
    ws['!cols'].push({ wch: Math.min(72, Math.max(10, Math.ceil(m + 2))) });
  }

  ws['!views'] = [
    {
      state: 'frozen',
      ySplit: headerOffset,
      xSplit: 0,
      topLeftCell: XLSX.utils.encode_cell({ r: headerOffset, c: 0 }),
      activeCell: XLSX.utils.encode_cell({ r: headerOffset, c: 0 }),
      showGridLines: true,
    },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Teknik Servisler');
  return wb;
}

/**
 * @param {{ open: boolean, onClose: () => void, exportArgs: { aktif: boolean, cityId: string, searchText: string, sortBy: string, sortOrder: string }, filterSummary: string }} props
 */
const TechServiceExcelExportModal = ({ open, onClose, exportArgs, filterSummary }) => {
  const [selectedKeys, setSelectedKeys] = useState(() =>
    TECH_SERVICE_EXPORT_COLUMNS.filter((c) => c.defaultOn || c.locked).map((c) => c.key)
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const groups = useMemo(() => {
    const g = new Map();
    TECH_SERVICE_EXPORT_COLUMNS.forEach((c) => {
      if (!g.has(c.group)) g.set(c.group, []);
      g.get(c.group).push(c);
    });
    return [...g.entries()];
  }, []);

  useEffect(() => {
    if (open) {
      setSelectedKeys(TECH_SERVICE_EXPORT_COLUMNS.filter((c) => c.defaultOn || c.locked).map((c) => c.key));
      setError('');
      setBusy(false);
    }
  }, [open]);

  const toggleKey = useCallback((key, col) => {
    if (col.locked) return;
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  const selectAllToggle = useCallback((on) => {
    setSelectedKeys(() => {
      if (on) return TECH_SERVICE_EXPORT_COLUMNS.map((c) => c.key);
      return TECH_SERVICE_EXPORT_COLUMNS.filter((c) => c.locked).map((c) => c.key);
    });
  }, []);

  const handleExport = async () => {
    const keysOrdered = TECH_SERVICE_EXPORT_COLUMNS.filter((c) => selectedKeys.includes(c.key)).map((c) => c.key);

    setBusy(true);
    setError('');
    try {
      const res = await fetchTechServicesExport({
        aktif: exportArgs?.aktif,
        cityId: exportArgs?.cityId,
        arama:
          exportArgs?.searchText && exportArgs.searchText.trim().length >= 3 ? exportArgs.searchText : undefined,
        sortBy: exportArgs?.sortBy,
        sortOrder: exportArgs?.sortOrder,
      });

      if (res?.basarili === false) {
        throw new Error(res?.mesaj || res?.message || 'Dışa aktarım başarısız');
      }

      const list = res?.veri?.teknikServisler || [];
      if (list.length === 0) {
        throw new Error('Bu filtrelere uyan teknik servis bulunamadı.');
      }

      const labels = TECH_SERVICE_EXPORT_COLUMNS.filter((c) => keysOrdered.includes(c.key)).map((c) => c.label);
      const rows = list.map((ts, idx) =>
        TECH_SERVICE_EXPORT_COLUMNS.filter((c) => keysOrdered.includes(c.key)).map((c) => pickCell(ts, idx, c.key))
      );

      const now = new Date().toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
      const metaLine1 = 'Teknik Servis raporu — Otomatik oluşturuldu';
      const metaLine2 = `Oluşturulma: ${now}${filterSummary ? ` | ${filterSummary}` : ''} | Kayıt: ${list.length}`;
      const wb = buildWorkbook(rows, labels, metaLine1, metaLine2, 4);
      const fname = `teknik-servisler_${new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-')}.xlsx`;
      XLSX.writeFile(wb, fname, { compression: true });
      if (window.showNotification) {
        window.showNotification('success', 'Excel dosyası indirildi.');
      }
      onClose?.();
    } catch (e) {
      setError(e?.message || 'Dışa aktarım sırasında hata oluştu.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="modal-overlay tech-service-export-overlay"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="modal tech-service-export-modal" onClick={(e) => e.stopPropagation()} role="document">
        <div className="modal-header">
          <h2>Excel’e aktar</h2>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body tech-service-export-modal__body">
          <p className="subtitle tech-service-export-modal__hint">
            Mevcut sekme ve filtrelerdeki kayıtların tamamı dışa aktarılır (sayfa ile sınırlı değil). İstediğiniz sütunları seçin.
          </p>
          <div className="tech-service-export-modal__bulk">
            <button type="button" className="secondary-btn" onClick={() => selectAllToggle(true)} disabled={busy}>
              Tümünü seç
            </button>
            <button type="button" className="secondary-btn" onClick={() => selectAllToggle(false)} disabled={busy}>
              Tümünü kaldır
            </button>
          </div>
          <div className="tech-service-export-modal__groups">
            {groups.map(([name, cols]) => (
              <div key={name} className="tech-service-export-modal__group">
                <div className="tech-service-export-modal__group-title">{name}</div>
                <div className="tech-service-export-modal__checkbox-grid">
                  {cols.map((c) => (
                    <label key={c.key} className="tech-service-export-modal__check">
                      <input
                        type="checkbox"
                        checked={selectedKeys.includes(c.key)}
                        disabled={busy || !!c.locked}
                        onChange={() => toggleKey(c.key, c)}
                      />
                      <span>{c.label}</span>
                      {c.locked ? (
                        <span className="tech-service-export-modal__locked-hint">(sabit)</span>
                      ) : null}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {error ? <div className="error-state small">{safeTxt(error)}</div> : null}
        </div>
        <div className="modal-footer">
          <button type="button" className="secondary-btn" onClick={onClose} disabled={busy}>
            İptal
          </button>
          <button type="button" className="primary-btn" onClick={handleExport} disabled={busy}>
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} aria-hidden />
                Hazırlanıyor...
              </>
            ) : (
              <>
                <i className="fas fa-file-excel" style={{ marginRight: 8 }} aria-hidden />
                Excel’e aktar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TechServiceExcelExportModal;
